/**
 * Parent-Teacher → Students lookup
 *
 * Resolves the logged-in parent-teacher's students via the PTA chain:
 *   auth.uid → users.id → parent_teachers.user_id → parent_teachers.id
 *   → pta_parent_teachers.parent_teacher_id → parent_teacher_assignments.id
 *   → parent_teacher_assignments.student_id → students.id
 *
 * Each `parent_teacher_assignments` row attaches the PT to exactly one
 * student (via `student_id`). The PT's student set is the union over
 * every PTA they're attached to via `pta_parent_teachers`.
 *
 * This matches the linkage already used by the backend dashboard
 * endpoints in services/api/src/routes/parent-teacher (commit b1979a4)
 * so server-rendered aggregates and client-side dropdowns / profile
 * deep-dives all see the same student set.
 */
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../queryClient";
import { supabase } from "../supabase";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "@utils/helper";

export interface ParentTeacherStudentLookup {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  // enrolment fields (active or latest student_grade_levels row)
  school_year_name?: string | null;
  grade_level_name?: string | null;
  curriculum_id?: string | null;
  grade_level_id?: string | null;
  school_year_id?: string | null;
}

const CASELOAD_SELECT =
  "parent_teachers!parent_teachers_user_id_fkey(pta_parent_teachers(parent_teacher_assignments(student_id, students(id, first_name, last_name, email, grade_level:student_grade_levels(active, updated_at, curriculum_id, grade_level_id, school_year_id, grade_level:grade_levels(grade_level), schoolYear:school_years(name))))))";

type OneOrMany<T> = T | T[] | null | undefined;

interface CaseloadRow {
  parent_teachers: OneOrMany<{
    pta_parent_teachers: OneOrMany<{
      parent_teacher_assignments: OneOrMany<{ student_id: string; students: unknown }>;
    }>;
  }>;
}

const asList = <T,>(value: OneOrMany<T>): T[] => (Array.isArray(value) ? value : value ? [value] : []);

const childOrder = (a: ParentTeacherStudentLookup, b: ParentTeacherStudentLookup) =>
  (a.first_name ?? "").localeCompare(b.first_name ?? "") ||
  (a.last_name ?? "").localeCompare(b.last_name ?? "") ||
  a.id.localeCompare(b.id);

export async function fetchMyPtStudentsLookup(authUserId: string): Promise<ParentTeacherStudentLookup[]> {
  const { data, error } = await supabase
    .from("users")
    .select(CASELOAD_SELECT)
    .eq("auth_user_id", authUserId)
    .maybeSingle<CaseloadRow>();

  if (error) throw error;

  const ptas = asList(data?.parent_teachers)
    .flatMap((pt) => asList(pt.pta_parent_teachers))
    .flatMap((junction) => asList(junction.parent_teacher_assignments));

  // De-dupe in case a student has multiple PTAs (different grade
  // levels / school years) that all map to the same PT.
  const seen = new Set<string>();
  const mapped: ParentTeacherStudentLookup[] = [];

  for (const row of ptas) {
    // `students` is a to-one embed; PostgREST may type it as an array.
    const s = Array.isArray((row as any).students)
      ? (row as any).students[0]
      : (row as any).students;
    if (!s || seen.has(s.id)) continue;
    seen.add(s.id);

    // Pick the active enrolment; fall back to most-recently-updated.
    const sglRaw = (s as any).grade_level;
    const sgls: any[] = Array.isArray(sglRaw) ? sglRaw : sglRaw ? [sglRaw] : [];
    const sorted = [...sgls].sort((a, b) => {
      if (a.active && !b.active) return -1;
      if (!a.active && b.active) return 1;
      return (b.updated_at ?? "") > (a.updated_at ?? "") ? 1 : -1;
    });
    const enrolment = sorted[0] ?? null;
    const gradeLevel = Array.isArray(enrolment?.grade_level)
      ? enrolment.grade_level[0]
      : enrolment?.grade_level;
    const schoolYear = Array.isArray(enrolment?.schoolYear)
      ? enrolment.schoolYear[0]
      : enrolment?.schoolYear;

    mapped.push({
      id: s.id,
      first_name: s.first_name ?? null,
      last_name: s.last_name ?? null,
      email: s.email ?? null,
      school_year_name: (schoolYear as any)?.name ?? null,
      grade_level_name: (gradeLevel as any)?.grade_level ?? null,
      curriculum_id: enrolment?.curriculum_id ?? null,
      grade_level_id: enrolment?.grade_level_id ?? null,
      school_year_id: enrolment?.school_year_id ?? null,
    });
  }

  return mapped.sort(childOrder);
}

const NO_ROWS: ParentTeacherStudentLookup[] = [];

export const ptCaseloadQueryKey = (authUserId: string | null) => ["parentTeacher", "caseload", authUserId];

export function useGetMyPtStudentsLookup() {
  const { session, loading: authLoading } = useAuth();
  const authUserId = session?.user?.id ?? null;
  const query = useQuery({
    queryKey: ptCaseloadQueryKey(authUserId),
    queryFn: () => fetchMyPtStudentsLookup(authUserId ?? ""),
    enabled: authUserId != null,
    retry: false,
  }, queryClient);

  if (!authUserId) {
    return { rows: NO_ROWS, loading: authLoading, error: null, refetch: query.refetch };
  }

  return {
    rows: query.data ?? NO_ROWS,
    loading: query.isPending,
    error: query.isError ? getErrorMessage(query.error, 'Failed to load students') : null,
    refetch: query.refetch,
  };
}
